import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import {
  CheckRow,
  CtaBand,
  FaqBlock,
  Label,
  PageHero,
  RelatedLinks,
  SectionHead,
} from "@/components/Shared";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { Stadtmusikanten, BremenSkyline } from "@/components/graphics/Marks";
import { SEO_PAGE_MAP } from "@/data/seoPages";
import { BRAND } from "@/lib/site";

const SeoPage = () => {
  const { pathname } = useLocation();
  const page = SEO_PAGE_MAP[pathname];

  if (!page) return null;

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Leistungen", to: "/leistungen" },
    { label: page.label.replace("/ ", ""), to: page.slug },
  ];

  return (
    <main data-testid={`seo-page${page.slug.replace(/\//g, "-")}`}>
      <Seo
        title={page.title}
        description={page.description}
        path={page.slug}
        breadcrumbs={crumbs}
        faq={page.faq}
        service={page.service}
        image={page.image}
      />

      <PageHero
        label={page.label}
        titleLines={page.h1Lines}
        intro={page.intro}
        image={page.image}
        breadcrumbs={crumbs}
        ctaPrimary={page.ctaPrimary}
        ctaSecondary={page.ctaSecondary}
      />

      {page.landing && (
        <section data-testid="landing-audience" className="border-b border-[#DCE5EF] bg-[#F5F8FC] py-16 lg:py-20">
          <div className="shell grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <SectionHead label={page.audienceLabel} title={page.audienceTitle} />
            <Stagger className="grid gap-3 sm:grid-cols-2">
              {page.audience.map((a) => (
                <StaggerItem key={a}>
                  <div className="flex h-full items-start gap-3 rounded-2xl border border-[#DCE5EF] bg-white p-5">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EEF6FF]">
                      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none">
                        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#0757B8" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="text-[14px] font-medium leading-relaxed text-[#10253A]">{a}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}


      {/* content sections */}
      <section className="bg-white py-16 lg:py-24">
        <div className="shell grid gap-14 lg:grid-cols-[1.45fr_0.8fr] lg:gap-20">
          <div className="min-w-0">
            {page.sections.map((s, i) => (
              <Reveal key={i} delay={0.04 * i} className={i > 0 ? "mt-14" : ""}>
                <h2 className="text-[22px] font-bold leading-snug sm:text-[27px] lg:text-[32px]">{s.h2}</h2>
                <div className="prose-de mt-5">
                  {s.p.map((t, j) => (
                    <p key={j} className="text-[15px] sm:text-base">
                      {t}
                    </p>
                  ))}
                </div>
                {s.bullets && (
                  <ul className="mt-7 grid gap-3 rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-6 sm:grid-cols-2">
                    {s.bullets.map((b, j) => (
                      <CheckRow key={j}>{b}</CheckRow>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}

            {/* Ablauf */}
            <Reveal className="mt-16">
              <Label>/ Ablauf</Label>
              <h2 className="mt-4 text-[22px] font-bold leading-snug sm:text-[27px] lg:text-[32px]">
                So läuft die Zusammenarbeit
              </h2>
              <ol className="mt-8 space-y-0 border-l border-[#DCE5EF] pl-0">
                {page.steps.map((st, i) => (
                  <li key={i} className="relative pb-8 pl-8 last:pb-0">
                    <span className="absolute -left-[11px] top-0.5 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#0757B8] font-mono text-[10px] font-bold text-white">
                      {i + 1}
                    </span>
                    <p className="text-[16px] font-semibold text-[#10253A]">{st.t}</p>
                    <p className="mt-1.5 text-[14.5px] leading-relaxed text-[#66788A]">{st.d}</p>
                  </li>
                ))}
              </ol>
            </Reveal>

            {/* Lokaler Bezug */}
            <Reveal className="relative mt-16 overflow-hidden rounded-2xl bg-[#062B52] p-7 text-white sm:p-10">
              <BremenSkyline className="pointer-events-none absolute inset-x-0 bottom-0 h-20 w-full opacity-[0.12]" color="#FFFFFF" />
              <Stadtmusikanten className="pointer-events-none absolute -right-6 bottom-0 h-44 opacity-[0.12]" color="#FFFFFF" />
              <div className="relative z-10">
                <Label tone="orange">/ Lokal in Bremen</Label>
                <h2 className="mt-4 text-[20px] font-bold sm:text-[25px]" style={{ color: "#fff" }}>
                  Bremen-Bezug: wo wir arbeiten
                </h2>
                <p className="mt-4 max-w-2xl text-[14.5px] leading-relaxed text-white/75 sm:text-[15px]">
                  {page.local}
                </p>
                <Link
                  to="/einsatzgebiet"
                  data-testid="seo-local-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B00]"
                >
                  <MapPin className="h-4 w-4" /> Einsatzgebiet ansehen
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* sidebar */}
          <aside className="min-w-0 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="rounded-2xl border border-[#DCE5EF] bg-white p-6 shadow-[0_30px_70px_-55px_rgba(6,43,82,0.6)]">
                <Label>/ Geeignete Objekte</Label>
                <ul className="mt-5 space-y-3">
                  {page.objects.map((o, i) => (
                    <CheckRow key={i}>{o}</CheckRow>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-5 rounded-2xl bg-[#0757B8] p-6 text-white">
                <p className="font-mono-label text-white/60">/ Direkt anfragen</p>
                <p className="mt-3 text-[19px] font-bold leading-snug" style={{ color: "#fff" }}>
                  Angebot auf Anfrage – für Ihr Objekt in Bremen.
                </p>
                <a href={BRAND.phoneHref} data-testid="seo-sidebar-phone" className="mt-5 block text-lg font-bold text-white">
                  {BRAND.phone}
                </a>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="seo-sidebar-whatsapp"
                  className="btn btn-wa mt-4 h-12 w-full text-sm"
                >
                  Per WhatsApp kontaktieren
                </a>
                <Link to="/kontakt" data-testid="seo-sidebar-cta" className="btn btn-orange mt-2.5 h-12 w-full text-sm">
                  Winterdienst anfragen
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-5 rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-6">
                <Label>/ Weitere Leistungen</Label>
                <Stagger className="mt-4 grid gap-2">
                  {page.related.slice(0, 5).map((r) => (
                    <StaggerItem key={r.to}>
                      <Link
                        to={r.to}
                        data-testid={`seo-sidebar-link${r.to.replace(/\//g, "-")}`}
                        className="group flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-[14px] font-medium text-[#10253A] transition-colors hover:text-[#0757B8]"
                      >
                        {r.title}
                        <ArrowUpRight className="h-3.5 w-3.5 text-[#FF6B00] transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {page.landing && (
        <section data-testid="landing-form" className="bg-white py-16 lg:py-24">
          <div className="shell grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <SectionHead
                label="/ Angebot anfordern"
                title={page.ctaPrimary}
                text="Angaben ausfüllen, absenden – die Anfrage geht direkt an unser Büro. Unverbindlich und kostenfrei; ein Vertrag entsteht erst mit Ihrer Zusage."
              />
              <div className="mt-7 grid gap-2.5">
                <a href={BRAND.phoneHref} data-testid="landing-form-phone" className="btn btn-dark h-13 py-3.5 text-[14px]">
                  {BRAND.phone}
                </a>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="landing-form-whatsapp"
                  className="btn btn-wa h-13 py-3.5 text-[14px]"
                >
                  Per WhatsApp anfragen
                </a>
              </div>
            </div>
            <ContactForm defaultType={page.formType} />
          </div>
        </section>
      )}

      <FaqBlock items={page.faq} title={`Fragen zu: ${page.label.replace("/ ", "")}`} />
      <CtaBand />
      <RelatedLinks items={page.related} />
    </main>
  );
};

export default SeoPage;
